import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useState, useRef } from "react";
import styles from "./excelImportModal.module.css";
import {
  useLazyGetExcelImportTemplateQuery,
  useLazyGetPatientsImportTemplateQuery,
  useUploadUsersMutation,
} from "../../../services/api/user.api";
import { saveBlobAs } from "../../../utils/fileDownload";
import { useAppDispatch, useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { showFeatureQuotaRestrictionPopup } from "../../../services/slices/accessControl.slice";
import download from "../../../images/download.svg";
import upload from "../../../images/upload.svg";

interface ExcelImportModalProps {
  onClose: () => void;
  userCategory: "clients" | "therapists" | "admins";
}

const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  onClose,
  userCategory,
}) => {
  const dispatch = useAppDispatch();
  const admin = useAppSelector(adminSelector);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    message: string;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadUsers, { isLoading }] = useUploadUsersMutation();
  const [getExcelImportTemplate] = useLazyGetExcelImportTemplateQuery();
  const [getPatientsImportTemplate] = useLazyGetPatientsImportTemplateQuery();

  // עמודות ההורה/אפוטרופוס קיימות רק בתבנית המטופלים (role 1),
  // ולכן מטפלים/אדמינים מקבלים את התבנית הכללית
  const isPatientsTemplate = userCategory === "clients";

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("companyId", String(admin.companyId));
    formData.append("userType", userCategory);
    try {
      const result = await uploadUsers(formData).unwrap();
      setUploadResult(result);
      console.log("Upload result:", result);
    } catch (err: any) {
      if (err.status === 429) {
        dispatch(
          showFeatureQuotaRestrictionPopup({
            feature: err.data?.feature ?? "",
            scope: err.data?.scope ?? null,
            limit: err.data?.limit ?? 0,
            used: err.data?.used ?? 0,
            resetsAt: err.data?.resetsAt ?? null,
          })
        );
        return;
      }
      setUploadResult({
        message: err.data?.message || t("ERROR_PROCESSING_FILE"),
        errors: err.data?.errors || [],
      });
    }
  };

  const handleDownload = async () => {
    const [getTemplate, defaultFileName] = isPatientsTemplate
      ? [getPatientsImportTemplate, "PatientsImportTemplate.xlsx"]
      : [getExcelImportTemplate, "UsersImportTemplate.xlsx"];

    try {
      const { blob, filename } = await getTemplate().unwrap();
      saveBlobAs(blob, filename || defaultFileName);
    } catch (err: any) {
      setUploadResult({
        message: err.data?.message || t("ERROR_PROCESSING_FILE"),
        errors: [],
      });
    }
  };
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>
        <h2>{t("IMPORT_USERS_FROM_EXCEL")}</h2>

        <div className={styles.step}>
          <h4>{t("STEP_1_DOWNLOAD_TEMPLATE")}</h4>
          <p>{t("DOWNLOAD_TEMPLATE_FILE")}</p>
          <a
            href="#"
            className={styles.actionButton}
            onClick={(e) => {e.preventDefault(); handleDownload()}}
          >
            <img src={download} alt="" className={styles.icon} />
            {isPatientsTemplate
              ? t("DOWNLOAD_PATIENTS_TEMPLATE")
              : t("DOWNLOAD_TEMPLATE")}
          </a>

          {isPatientsTemplate && (
            <p className={styles.templateHint}>
              {t("PATIENTS_TEMPLATE_PARENT_COLUMNS_HINT")}
            </p>
          )}
        </div>

        <div className={styles.step}>
          <h4>{t("STEP_2_UPLOAD_FILE")}</h4>
          <p>{t("SELECT_FULL_EXCEL_FILE")}</p>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <button
            className={styles.actionButton}
            onClick={() => fileInputRef.current?.click()}
          >
            <img
              src={upload}
              alt={t("UPLOAD_FILE_1")}
              className={styles.icon}
            />
            {selectedFile ? selectedFile.name : t("CHOOSE_FILE_1")}
          </button>

          {selectedFile && (
            <button
              className={styles.uploadButton}
              onClick={handleUpload}
              disabled={isLoading}
            >
              {isLoading ? t("LABORATORY_EXPERIMENT") : t("HAALAH_VEYEBA")}
            </button>
          )}
        </div>

        {uploadResult && (
          <div className={styles.results}>
            <h4>{t("IMPORT_SUMMARY")}</h4>
            <p
              className={
                uploadResult.errors?.length > 0
                  ? styles.resultMessageError
                  : styles.resultMessageSuccess
              }
            >
              {uploadResult.message}
            </p>
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div className={styles.errorDetails}>
                <h5>{t("ERROR_DETAILS")}</h5>
                <ul className={styles.errorList}>
                  {uploadResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelImportModal;
